import React, { useEffect, useRef, useState } from 'react'
import { Platform, ScrollView, Text, View } from 'react-native'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SafeAreaView } from 'react-native-safe-area-context'

import { USER_SUBMIT_KYC_LV1 } from '@/constants/graphql/mutations'
import {
  GET_USER,
  USER_KYC_LV1_IMAGE_UPLOAD_PRESIGNED_PUTS,
  USER_KYC_LV1_VIDEO_UPLOAD_PRESIGNED_PUTS
} from '@/constants/graphql/queries'
import { AddImageInterface, ImageAsset } from '@/types/common'
import { preparePickedImageUpload } from '@/utils/imageUpload'
import {
  createKycDiagnostics,
  getUriDiagnosticMetadata,
  startKycDiagnosticStage,
  type KycDiagnosticStage
} from '@/utils/kycDiagnostics'
import { launchMediaLibraryAsync } from '@/utils/mediaPicker'
import { generateRandomFileName, getFileNameFromUrl, uploadImageToPresigned } from '@/utils/url'
import { Button, useToast } from '@/components/base'
import { RecordIllustration, Spinner } from '@/components/icon'
import { useImageViewer } from '@/components/ImageViewer'
import { KYCFormSection } from '@/components/kyc/FormSection'
import { KYCRecordingSection } from '@/components/kyc/RecordingSection'
import KycHeader from '@/components/kycHeader'
import { Stack } from '@/components/layout'

enum Steps {
  FillForm,
  PrepareRecordVideo,
  RecordAndSubmit,
  Submitted
}

type KYCFormValues = {
  country: string
  lastname: string
  firstname: string
  governmentId: string
  images: ImageAsset[]
  video: string
}

const defaultValues: KYCFormValues = {
  country: '',
  lastname: '',
  firstname: '',
  governmentId: '',
  images: [],
  video: ''
}

export default function KYCSubmittingScreen({ setSuccess }: { setSuccess: () => void }) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError, watch } = useForm<KYCFormValues>({
    defaultValues: { ...defaultValues, country: t('kyc:defaultCountry') }
  })
  const [diagnostics] = useState(() => createKycDiagnostics('kyc-lv1'))
  const toast = useToast()
  const [formKey, setFormKey] = useState(0)
  const { setSource, onDeleteCallback } = useImageViewer()
  const [imageUploadedNames, setImageUploadedNames] = useState<{ [index: number]: string }>({})
  const [videoUploadName, setVideoUploadName] = useState('')
  const [currentStep, setCurrentStep] = useState(Steps.FillForm)
  const [submittedValues, setSubmittedValues] = useState<KYCFormValues>(defaultValues)
  const [videoBlob, setVideoBlob] = useState<Blob | undefined>(undefined)
  const [errorMsg, setErrorMsg] = useState('')
  const [retryMode, setRetryMode] = useState<'form' | 'uploads'>('uploads')
  const imagePresignStageRef = useRef<KycDiagnosticStage | null>(null)
  const imageUploadStageRef = useRef<KycDiagnosticStage | null>(null)
  const videoPresignStageRef = useRef<KycDiagnosticStage | null>(null)
  const videoReadStageRef = useRef<KycDiagnosticStage | null>(null)
  const videoUploadStageRef = useRef<KycDiagnosticStage | null>(null)
  const submitStageRef = useRef<KycDiagnosticStage | null>(null)
  const submitStartedRef = useRef(false)
  const imagePresignIndexRef = useRef<number | null>(null)
  const currentStepRef = useRef(currentStep)
  const isValid = watch('country') && watch('firstname') && watch('lastname') && watch('governmentId')

  currentStepRef.current = currentStep

  useEffect(() => {
    diagnostics.event('kyc-flow-mounted', { platform: Platform.OS, platformVersion: String(Platform.Version) })
    return () => diagnostics.event('kyc-flow-unmounted', { lastStep: Steps[currentStepRef.current] })
  }, [diagnostics])

  useEffect(() => {
    diagnostics.event('kyc-step-changed', { step: Steps[currentStep] })
  }, [currentStep, diagnostics])

  const [getImageUploadsPresigned] = useLazyQuery(USER_KYC_LV1_IMAGE_UPLOAD_PRESIGNED_PUTS, {
    fetchPolicy: 'no-cache',
    onCompleted: async (response) => {
      const uploadUrl = response.userKycLv1ImageUploadPresignedPuts
      const requestedIndex = imagePresignIndexRef.current
      let fileName
      let displayName
      try {
        fileName = getFileNameFromUrl(uploadUrl)
        displayName = decodeURIComponent(fileName)
        imagePresignStageRef.current?.complete({
          imageIndex: requestedIndex,
          responseHasUploadUrl: Boolean(uploadUrl)
        })
      } catch (error) {
        imagePresignStageRef.current?.fail(error, { imageIndex: requestedIndex })
        diagnostics.error('kyc-image-presign-response-invalid', error, { imageIndex: requestedIndex })
        setErrorMsg(t('kyc:uploadFailed'))
        return null
      }

      const idx = requestedIndex ?? images.findIndex((image) => displayName === image.fileName)
      const imageAsset = images[idx]
      if ((idx !== 0 && idx !== 1) || !imageAsset) {
        const error = new Error('Unable to match the KYC image upload response to a selected image')
        diagnostics.error('kyc-image-upload-asset-missing', error, { imageIndex: idx })
        setErrorMsg(t('kyc:uploadFailed'))
        return null
      }

      imageUploadStageRef.current = startKycDiagnosticStage(diagnostics, 'kyc-image-upload', {
        imageIndex: idx,
        mimeType: imageAsset.mimeType ?? 'unknown',
        ...getUriDiagnosticMetadata(imageAsset.uri)
      })
      try {
        await uploadImageToPresigned(imageAsset, fileName, uploadUrl)
        imageUploadStageRef.current.complete({ imageIndex: idx })
        setImageUploadedNames((prev) => ({
          ...prev,
          [idx]: displayName
        }))
      } catch (error) {
        imageUploadStageRef.current.fail(error, { imageIndex: idx })
        setErrorMsg(t('kyc:uploadFailed'))
        return null
      }

      if (idx < 1) {
        const nextIdx = idx + 1
        requestImagePresign(nextIdx)
      }
    },
    onError: (error) => {
      imagePresignStageRef.current?.fail(error, {
        imageIndex: imagePresignIndexRef.current,
        graphQLErrorCount: error.graphQLErrors.length,
        hasNetworkError: Boolean(error.networkError)
      })
      setErrorMsg(t('kyc:uploadFailed'))
    }
  })

  const [getVideoUploadPresigned] = useLazyQuery(USER_KYC_LV1_VIDEO_UPLOAD_PRESIGNED_PUTS, {
    onCompleted: async (response) => {
      const uploadUrl = response.userKycLv1VideoUploadPresignedPuts
      let fileName
      let displayName
      try {
        fileName = getFileNameFromUrl(uploadUrl)
        displayName = decodeURIComponent(fileName)
        videoPresignStageRef.current?.complete({ responseHasUploadUrl: Boolean(uploadUrl) })
      } catch (error) {
        videoPresignStageRef.current?.fail(error)
        diagnostics.error('kyc-video-presign-response-invalid', error)
        setErrorMsg(t('kyc:uploadFailed'))
        return null
      }

      let blob: Blob
      videoReadStageRef.current = startKycDiagnosticStage(diagnostics, 'kyc-video-file-read', {
        inMemoryBlobAvailable: Boolean(videoBlob),
        ...getUriDiagnosticMetadata(video)
      })
      try {
        if (videoBlob) {
          blob = videoBlob
        } else {
          const videoResponse = await fetch(video)
          diagnostics.event('kyc-video-file-response-received', {
            responseOk: videoResponse.ok,
            responseStatus: videoResponse.status
          })
          blob = await videoResponse.blob()
        }
        videoReadStageRef.current.complete({
          fileSizeBytes: blob.size,
          mimeType: blob.type || 'unknown'
        })
      } catch (error) {
        videoReadStageRef.current.fail(error)
        setErrorMsg(t('kyc:uploadFailed'))
        return null
      }

      videoUploadStageRef.current = startKycDiagnosticStage(diagnostics, 'kyc-video-upload', {
        fileSizeBytes: blob.size,
        mimeType: blob.type || 'unknown',
        uploadFileExtension: getUriDiagnosticMetadata(displayName).extension
      })
      try {
        const resp = await fetch(uploadUrl, {
          headers: {
            'Content-Type': blob.type || 'video/mp4',
            'Content-Disposition': `attachment; filename="${displayName}"`
          },
          method: 'PUT',
          body: blob
        })

        diagnostics.event('kyc-video-upload-response-received', {
          responseOk: resp.ok,
          responseStatus: resp.status
        })
        if (resp.status !== 200) {
          throw new Error('Error uploading video')
        }

        videoUploadStageRef.current.complete({ responseStatus: resp.status })
        setVideoUploadName(displayName)
      } catch (error) {
        videoUploadStageRef.current.fail(error)
        setErrorMsg(t('kyc:uploadFailed'))
      }
    },
    onError: (error) => {
      videoPresignStageRef.current?.fail(error, {
        graphQLErrorCount: error.graphQLErrors.length,
        hasNetworkError: Boolean(error.networkError)
      })
      setErrorMsg(t('kyc:uploadFailed'))
    }
  })

  const [submitKYC] = useMutation(USER_SUBMIT_KYC_LV1, {
    onCompleted: () => {
      submitStageRef.current?.complete()
      setSuccess()
    },
    onError: (error) => {
      const { graphQLErrors } = error
      const errorCode = graphQLErrors[0]?.extensions?.code

      submitStageRef.current?.fail(error, {
        errorCode: typeof errorCode === 'number' || typeof errorCode === 'string' ? String(errorCode) : undefined,
        graphQLErrorCount: graphQLErrors.length,
        hasNetworkError: Boolean(error.networkError)
      })
      if (errorCode === 2016) {
        setErrorMsg(t('kyc:identityAlreadyVerified'))
      } else {
        setErrorMsg(t('kyc:verificationFailed'))
      }
      setRetryMode('form')
    },
    refetchQueries: [GET_USER]
  })

  function requestImagePresign(index: number) {
    const image = images[index]
    if (!image) {
      diagnostics.error('kyc-image-presign-blocked', new Error('The selected KYC image is missing'), {
        imageIndex: index
      })
      setErrorMsg(t('kyc:uploadFailed'))
      return
    }

    let fileName: string
    try {
      fileName = image.fileName || getFileNameFromUrl(image.uri)
    } catch (error) {
      diagnostics.error('kyc-image-filename-unavailable', error, {
        imageIndex: index,
        ...getUriDiagnosticMetadata(image.uri)
      })
      setErrorMsg(t('kyc:uploadFailed'))
      return
    }

    imagePresignIndexRef.current = index
    imagePresignStageRef.current = startKycDiagnosticStage(diagnostics, 'kyc-image-presign', {
      imageIndex: index,
      mimeType: image.mimeType ?? 'unknown',
      ...getUriDiagnosticMetadata(image.uri)
    })
    getImageUploadsPresigned({ variables: { fileName } }).catch((error: unknown) => {
      imagePresignStageRef.current?.fail(error, { imageIndex: index })
      setErrorMsg(t('kyc:uploadFailed'))
    })
  }

  function requestVideoPresign() {
    const extension = getKycVideoUploadExtension(video, videoBlob)
    videoPresignStageRef.current = startKycDiagnosticStage(diagnostics, 'kyc-video-presign', {
      recordedVideoPresent: Boolean(video),
      inMemoryBlobAvailable: Boolean(videoBlob),
      uploadExtension: extension,
      ...getUriDiagnosticMetadata(video)
    })
    getVideoUploadPresigned({ variables: { fileName: `${generateRandomFileName()}.${extension}` } }).catch(
      (error: unknown) => {
        videoPresignStageRef.current?.fail(error)
        setErrorMsg(t('kyc:uploadFailed'))
      }
    )
  }

  function handlePrevStep() {
    diagnostics.event('kyc-previous-step-pressed', {
      fromStep: Steps[currentStep],
      toStep: Steps[currentStep - 1]
    })
    setCurrentStep(currentStep - 1)
  }

  function handleNextStep() {
    diagnostics.event('kyc-next-step-pressed', {
      fromStep: Steps[currentStep],
      imageCount: images.filter(Boolean).length,
      formFieldsComplete: Boolean(isValid)
    })
    if (currentStep === Steps.FillForm) {
      if (!images[0] || !images[1]) {
        diagnostics.warn('kyc-next-step-blocked', { reason: 'missing-government-id-images' })
        setError('images', { message: t('kyc:uploadIdentityPhotos') })
        return
      }
    }
    setCurrentStep(currentStep + 1)
  }

  const onSubmit = async (data: KYCFormValues) => {
    diagnostics.event('kyc-form-submit-accepted', {
      imageCount: images.filter(Boolean).length,
      recordedVideoPresent: Boolean(video),
      formFieldsComplete: Boolean(data.country && data.firstname && data.lastname && data.governmentId),
      firstImageAlreadyUploaded: Boolean(imageUploadedNames[0]),
      secondImageAlreadyUploaded: Boolean(imageUploadedNames[1]),
      videoAlreadyUploaded: Boolean(videoUploadName)
    })
    setSubmittedValues({ ...data })
    setRetryMode('uploads')
    setCurrentStep(Steps.Submitted)

    startMediaUploads()
  }

  function startMediaUploads() {
    try {
      if (!imageUploadedNames[0]) requestImagePresign(0)
      if (!videoUploadName) requestVideoPresign()
    } catch (error) {
      diagnostics.error('kyc-upload-orchestration-failed', error)
      setError('root', { message: t('kyc:uploadFailed') })
      setErrorMsg(t('kyc:uploadFailed'))
    }
  }

  const {
    field: { value: images, onChange: onChangeImages }
  } = useController({ control, name: 'images' })
  const {
    field: { value: video, onChange: onChangeVideo }
  } = useController({ control, name: 'video' })

  async function pickImage(index: number) {
    diagnostics.event('kyc-image-picker-opened', { imageIndex: index })
    try {
      const result = await launchMediaLibraryAsync({
        mediaTypes: ['images'],
        quality: 1
      })
      if (result.canceled) {
        diagnostics.event('kyc-image-picker-cancelled', { imageIndex: index })
        return
      }

      const selected = result.assets[0]
      diagnostics.event('kyc-image-picker-returned', {
        imageIndex: index,
        fileSizeBytes: selected.fileSize,
        mimeType: selected.mimeType ?? 'unknown',
        width: selected.width,
        height: selected.height,
        ...getUriDiagnosticMetadata(selected.uri)
      })
      const prepared = await preparePickedImageUpload(result.assets[0])
      if (!prepared.ok) {
        diagnostics.warn('kyc-image-preparation-rejected', { imageIndex: index, reason: prepared.reason })
        toast.show({
          variant: 'error',
          description: prepared.reason === 'type' ? t('kyc:invalidImageFormat') : t('kyc:imageTooLarge')
        })
        return
      }
      diagnostics.event('kyc-image-preparation-completed', {
        imageIndex: index,
        mimeType: prepared.asset.mimeType ?? 'unknown',
        ...getUriDiagnosticMetadata(prepared.asset.uri)
      })
      addImage(prepared.asset.uri, index, prepared.asset.fileName, prepared.asset.mimeType)
    } catch (error) {
      diagnostics.error('kyc-image-picker-or-preparation-failed', error, { imageIndex: index })
      toast.show({ variant: 'error', description: t('kyc:unableToReadImage') })
    }
  }

  useEffect(() => {
    if (!(imageUploadedNames[0] && imageUploadedNames[1] && videoUploadName)) return
    if (submitStartedRef.current) return

    submitStartedRef.current = true
    diagnostics.event('kyc-all-media-uploads-completed', {
      firstImageUploaded: true,
      secondImageUploaded: true,
      videoUploaded: true
    })
    submitStageRef.current = startKycDiagnosticStage(diagnostics, 'kyc-graphql-submit')
    submitKYC({
      variables: {
        firstname: submittedValues.firstname,
        lastname: submittedValues.lastname,
        governmentId: submittedValues.governmentId,
        videoBucketFileName: videoUploadName,
        frontGovernmentIdFileName: imageUploadedNames[0],
        backGovernmentIdFileName: imageUploadedNames[1],
        countryRegion: submittedValues.country
      }
    }).catch((error: unknown) => {
      submitStageRef.current?.fail(error)
      setRetryMode('form')
      setErrorMsg(t('kyc:verificationFailed'))
    })
  }, [
    diagnostics,
    imageUploadedNames,
    submitKYC,
    submittedValues.country,
    submittedValues.firstname,
    submittedValues.governmentId,
    submittedValues.lastname,
    t,
    videoUploadName
  ])

  const addImage: AddImageInterface = (
    uri: string,
    index: number,
    fileName?: string | null,
    mimeType?: string | null
  ) => {
    const extension = typeof fileName === 'string' ? fileName.split('.').at(-1) : 'jpg'
    const name = `${generateRandomFileName()}.${extension}`
    const asset = {
      uri,
      fileName: name,
      mimeType
    }

    diagnostics.event('kyc-image-added-to-form', {
      imageIndex: index,
      uploadExtension: extension?.toLowerCase() ?? 'unknown',
      mimeType: mimeType ?? 'unknown',
      ...getUriDiagnosticMetadata(uri)
    })

    const nextImages = [...images]
    nextImages[index] = asset
    onChangeImages(nextImages)
  }

  function deleteImage(deleteIndex: number) {
    if (deleteIndex === -1) {
      diagnostics.warn('kyc-image-delete-ignored', { reason: 'image-not-found' })
      return
    }
    diagnostics.event('kyc-image-deleted', { imageIndex: deleteIndex })
    const nextImages = []
    const remainingIndex = deleteIndex === 0 ? 1 : 0
    nextImages[remainingIndex] = images[remainingIndex]
    onChangeImages(nextImages)
  }

  function handleViewAddImage(index: number) {
    if (images[index]) {
      diagnostics.event('kyc-image-preview-opened', { imageIndex: index })
      setSource(images[index].uri)
      onDeleteCallback.current = () => deleteImage(index)
    } else {
      pickImage(index)
    }
  }

  function addVideo(uri: string, blob?: Blob) {
    diagnostics.event('kyc-recorded-video-added', {
      inMemoryBlobAvailable: Boolean(blob),
      fileSizeBytes: blob?.size,
      mimeType: blob?.type || 'unknown',
      ...getUriDiagnosticMetadata(uri)
    })
    onChangeVideo(uri)
    setVideoBlob(blob)
  }

  function deleteVideo() {
    diagnostics.event('kyc-recorded-video-deleted', { recordedVideoPresent: Boolean(video) })
    onChangeVideo('')
    setVideoBlob(undefined)
  }

  function resetKey() {
    diagnostics.event('kyc-recording-component-reset')
    setFormKey(Math.random())
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => (
            <KycHeader
              handlePrevStep={
                currentStep !== Steps.FillForm && currentStep !== Steps.Submitted ? handlePrevStep : undefined
              }
            />
          )
        }}
      />
      <SafeAreaView className={'flex-1 bg-background-primary'} edges={['left', 'right', 'bottom']}>
        {currentStep === Steps.FillForm && (
          <KYCFormSection
            control={control}
            handleNextStep={handleNextStep}
            handleViewAddImage={handleViewAddImage}
            images={images}
            readyForSubmission={!!(images[0] && images[1] && isValid)}
          />
        )}
        {currentStep === Steps.PrepareRecordVideo && (
          <ScrollView className={'flex-1'} contentContainerStyle={{ flexGrow: 1 }}>
            <View className={'grow p-4'}>
              <View>
                <RecordIllustration className={'w-full'} />
              </View>
              <View className={'h-3'} />
              <Text className={'text-sm text-text-secondary'}>
                {t('kyc:prepareVideo')}
              </Text>
              <View className={'h-6'} />
              <Button intent={'primary'} variant={'solid'} size={'md'} onPress={handleNextStep}>
                {t('kyc:startRecording')}
              </Button>
              <View className={'h-4'} />
            </View>
          </ScrollView>
        )}
        {currentStep === Steps.RecordAndSubmit && (
          <KYCRecordingSection
            key={formKey}
            videoUri={video}
            diagnostics={diagnostics}
            onSubmit={handleSubmit(onSubmit)}
            addVideo={addVideo}
            deleteVideo={deleteVideo}
            resetKey={resetKey}
          />
        )}
        {currentStep === Steps.Submitted && (
          <View className={'flex-1 items-center justify-center'}>
            <View className={'mb-3 size-6 animate-spin items-center justify-center'}>
              <Spinner className={'size-6 text-primary'} />
            </View>
            <Text className={'text-sm text-text-primary'}>{errorMsg || t('kyc:uploading')}</Text>
            {errorMsg && (
              <Button
                className={'mt-4'}
                intent={'primary'}
                variant={'solid'}
                size={'md'}
                onPress={() => {
                  diagnostics.event('kyc-retry-pressed')
                  if (retryMode === 'uploads') {
                    setErrorMsg('')
                    startMediaUploads()
                    return
                  }
                  setCurrentStep(Steps.FillForm)
                  setErrorMsg('')
                  reset()
                  setImageUploadedNames({})
                  setVideoUploadName('')
                  setVideoBlob(undefined)
                  submitStartedRef.current = false
                }}
              >
                {t(retryMode === 'uploads' ? 'kyc:retryUpload' : 'kyc:fillInAgain')}
              </Button>
            )}
          </View>
        )}
      </SafeAreaView>
    </>
  )
}

function getKycVideoUploadExtension(videoUri: string, videoBlob: Blob | undefined) {
  const mimeType = videoBlob?.type?.toLowerCase().split(';', 1)[0]
  if (mimeType === 'video/mp4') return 'mp4'
  if (mimeType === 'video/quicktime') return 'mov'
  if (mimeType === 'video/webm') return 'webm'

  const extension = getUriDiagnosticMetadata(videoUri).extension?.toLowerCase() ?? ''
  if (extension === 'mp4' || extension === 'mov' || extension === 'webm') return extension

  return Platform.OS === 'web' ? 'webm' : 'mp4'
}
